import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SIGNUP_STATUS_DEFS,
  SIGNUP_STATUS_MAP,
  TENANT_STATUS_MAP,
  type SignupStatus,
  type TenantStatus,
} from '@goyone/shared';
import { dateTime, plural, relative, WORDS } from '@ui/lib/format';
import { Badge, EmptyState, ErrorState, Modal, PageHead, Pager, Stat, TableSkeleton } from '@ui/components/ui';
import { Icon } from '@ui/components/Icon';
import { api, qs } from '../lib/api';

interface SignupRow {
  id: string;
  companyName: string;
  contactName: string;
  email: string;
  phone: string | null;
  requestedSlug: string | null;
  planCode: string;
  planName: string;
  employeeCount: string | null;
  industry: string | null;
  message: string | null;
  status: SignupStatus;
  source: string;
  notes: string | null;
  handledBy: string | null;
  convertedTenantId: string | null;
  createdAt: string;
  tenant: { id: string; slug: string; status: TenantStatus; trialEndsAt: string | null } | null;
}

interface SignupPage {
  items: SignupRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  counts: Partial<Record<SignupStatus, number>>;
}

/**
 * طلبات التسجيل.
 *
 * كل من ملأ نموذج الموقع يصير سطرًا هنا. الشاشة ليست سجلًا للأرشيف بل
 * قائمة عمل: أغلب من يبدأ تجربة لا يشترك، ومتابعتهم قبل انتهائها هي ما
 * يحوّل التجربة إلى اشتراك.
 */
export function Signups() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SignupStatus | ''>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<SignupRow | null>(null);

  const query = { q: search || undefined, status: status || undefined, page };

  const list = useQuery({
    queryKey: ['signups', query],
    queryFn: () => api<SignupPage>(`/signups${qs(query)}`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['signups'] });
    setOpen(null);
  };

  const counts = list.data?.counts ?? {};

  return (
    <div className="page">
      <PageHead
        title="طلبات التسجيل"
        subtitle="من سجّل من الموقع ومن يحتاج متابعة"
      />

      <div className="stats">
        <Stat label="الإجمالي" value={list.data?.total ?? '—'} />
        <Stat
          label="جديدة"
          value={<span className={counts.NEW ? 'neg' : ''}>{counts.NEW ?? 0}</span>}
          note="لم تُراجَع بعد"
        />
        <Stat label="في التجربة" value={counts.TRIALING ?? 0} note="تحتاج متابعة قبل الانتهاء" />
        <Stat
          label="تحوّلت"
          value={<span className="pos">{counts.CONVERTED ?? 0}</span>}
          note="صارت مشتركة"
        />
      </div>

      <div className="toolbar">
        <input
          className="input"
          value={search}
          placeholder="بحث بالشركة أو الاسم أو البريد"
          aria-label="بحث"
          style={{ maxWidth: 320 }}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        <div className="row" style={{ gap: 2 }}>
          <button
            className={`btn btn-sm${!status ? ' btn-primary' : ''}`}
            onClick={() => {
              setStatus('');
              setPage(1);
            }}
          >
            الكل
          </button>
          {SIGNUP_STATUS_DEFS.map((s) => (
            <button
              key={s.key}
              className={`btn btn-sm${status === s.key ? ' btn-primary' : ''}`}
              title={s.hint}
              onClick={() => {
                setStatus(s.key);
                setPage(1);
              }}
            >
              {s.label}
              {counts[s.key] ? <span className="tiny mute"> {counts[s.key]}</span> : null}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>الشركة</th>
                <th>جهة الاتصال</th>
                <th>الباقة</th>
                <th>الحالة</th>
                <th>الشركة على المنصة</th>
                <th>منذ</th>
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={6} />}

            {list.data && (
              <tbody>
                {list.data.items.map((r) => (
                  <tr key={r.id} className="clickable" onClick={() => setOpen(r)}>
                    <td>
                      <span className="cell-strong">{r.companyName}</span>
                      {r.industry && <div className="tiny mute">{r.industry}</div>}
                    </td>
                    <td>
                      {r.contactName}
                      <div className="tiny mute num">{r.email}</div>
                    </td>
                    <td className="cell-mute">{r.planName}</td>
                    <td>
                      <Badge kind={SIGNUP_STATUS_MAP[r.status].kind}>
                        {SIGNUP_STATUS_MAP[r.status].label}
                      </Badge>
                    </td>
                    <td>
                      {r.tenant ? (
                        <Link
                          to={`/tenants/${r.tenant.id}`}
                          className="link num"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {r.tenant.slug}
                        </Link>
                      ) : (
                        <span className="cell-mute">—</span>
                      )}
                      {r.tenant && (
                        <div className="tiny mute">
                          {TENANT_STATUS_MAP[r.tenant.status].label}
                        </div>
                      )}
                    </td>
                    <td className="cell-mute" title={dateTime(r.createdAt)}>
                      {relative(r.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا طلبات"
            text={
              search || status
                ? 'لا نتائج لهذه التصفية.'
                : 'الطلبات القادمة من صفحة التسجيل على الموقع ستظهر هنا.'
            }
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={setPage}
          />
        )}
      </div>

      {open && <SignupModal request={open} onClose={() => setOpen(null)} onSaved={refresh} />}
    </div>
  );
}

/* ========================================================================== */

function SignupModal({
  request,
  onClose,
  onSaved,
}: {
  request: SignupRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState<SignupStatus>(request.status);
  const [notes, setNotes] = useState(request.notes ?? '');

  const save = useMutation({
    mutationFn: () =>
      api(`/signups/${request.id}`, {
        method: 'PATCH',
        body: { status, notes: notes || null },
      }),
    onSuccess: onSaved,
  });

  const trialLeft = request.tenant?.trialEndsAt
    ? Math.ceil((new Date(request.tenant.trialEndsAt).getTime() - Date.now()) / 86_400_000)
    : null;

  return (
    <Modal
      title={request.companyName}
      onClose={onClose}
      wide
      footer={
        <>
          {request.tenant && (
            <Link to={`/tenants/${request.tenant.id}`} className="btn me-auto">
              فتح ملف الشركة
            </Link>
          )}
          <button className="btn" onClick={onClose}>
            إغلاق
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            حفظ
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      {request.tenant && trialLeft !== null && (
        <div className={`alert mb-4 ${trialLeft <= 3 ? 'alert-warning' : 'alert-info'}`}>
          {trialLeft > 0
            ? `التجربة تنتهي بعد ${plural(trialLeft, WORDS.day)}. هذه أفضل نافذة للتواصل.`
            : 'التجربة انتهت. الحساب في مهلة سداد أو موقوف.'}
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <span className="label">جهة الاتصال</span>
          <span>{request.contactName}</span>
        </div>
        <div className="field">
          <span className="label">الباقة المطلوبة</span>
          <span>{request.planName}</span>
        </div>
        <div className="field">
          <span className="label">البريد</span>
          <a className="num link" href={`mailto:${request.email}`} dir="ltr">
            {request.email}
          </a>
        </div>
        <div className="field">
          <span className="label">الهاتف</span>
          {request.phone ? (
            <a className="num link" href={`tel:${request.phone}`} dir="ltr">
              {request.phone}
            </a>
          ) : (
            <span>—</span>
          )}
        </div>
        <div className="field">
          <span className="label">حجم الفريق</span>
          <span>{request.employeeCount ?? '—'}</span>
        </div>
        <div className="field">
          <span className="label">مجال العمل</span>
          <span>{request.industry ?? '—'}</span>
        </div>
        <div className="field">
          <span className="label">المصدر</span>
          <span>{request.source === 'website' ? 'الموقع' : request.source}</span>
        </div>
        <div className="field">
          <span className="label">تاريخ الطلب</span>
          <span>{dateTime(request.createdAt)}</span>
        </div>
      </div>

      {request.message && (
        <>
          <h4 className="section-title">ما يريده العميل</h4>
          <p className="small">{request.message}</p>
        </>
      )}

      <h4 className="section-title">المتابعة</h4>
      <div className="form-grid">
        <div className="field">
          <label className="label" htmlFor="sg-status">
            الحالة
          </label>
          <select
            id="sg-status"
            className="select"
            value={status}
            onChange={(e) => setStatus(e.target.value as SignupStatus)}
          >
            {SIGNUP_STATUS_DEFS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
          <span className="tiny mute">{SIGNUP_STATUS_MAP[status].hint}</span>
        </div>
      </div>

      <div className="field mt-3">
        <label className="label" htmlFor="sg-notes">
          ملاحظات المتابعة
        </label>
        <textarea
          id="sg-notes"
          className="textarea"
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="ما دار في المكالمة، والاعتراضات، وموعد المتابعة التالي"
        />
      </div>

      {status === 'CONVERTED' && request.tenant && (
        <div className="alert alert-info mt-4">
          <Icon name="alert" size={14} />
          تعليم الطلب "مشترك" لا يفعّل الاشتراك. فعّله من ملف الشركة ليبدأ العدّ وتُصدر
          الفواتير.
        </div>
      )}
    </Modal>
  );
}
